import { Typography } from '@mui/material';
import { gridStringOrNumberComparator } from '@mui/x-data-grid-pro';
import { compact, first, isNil } from 'lodash';

import { SearchSmIcon } from '@/components/icons/SearchSmIcon';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { PopperContent } from '@/components/poppers/PopperContent';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { Column } from '@/components/tables/DataTable/types';
import { BENEFICIARY_LEVEL_CURRENT_ACCESS_DISPLAY_NAMES } from '@/modules/beneficiaries/beneficiaries.constants';
import { ContextualHelpTooltip } from '@/modules/content/components/ContextualHelpTooltip';
import { sortBeneficiariesByLevel } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.utils';
import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';
import {
  AccessParameterFrequency,
  AccessParameterKind,
  EntityKind,
  ScheduledDistributionFrequency,
  ScheduledDistributionKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { UnreachableError } from '@/utils/errors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import {
  formatPercent,
  formatPercentNoDecimals,
} from '@/utils/formatting/percent';
import { getNodes } from '@/utils/graphqlUtils';

import { RowData } from './ExpandableBeneficiaryTable.types';
import {
  TrustBeneficiariesList_AccessParameterFragment,
  TrustBeneficiariesList_BeneficiaryFragment,
  TrustBeneficiariesList_ScheduledDistributionFragment,
} from './graphql/TrustBeneficiariesList.fragments.generated';
import { createBeneficiaryRow } from './TrustBeneficiariesList.utils';
import { getBeneficiariesGroupHeading } from './TrustBeneficiaryListV2.utils';

interface GetColumnsInput {
  heading?: string;
  entityKind: EntityKind | null | undefined;
}

export function getColumns(input: GetColumnsInput): Column<RowData>[] {
  const heading = getBeneficiariesGroupHeading(input);

  return [
    {
      field: 'beneficiary',
      headerName: heading,
      flex: 1,
      renderCell: TwoLineTextRenderer({
        lineOne: ({ row }) => row.beneficiary.lineOne,
        lineOneProps: ({ row }) => row.beneficiary.lineOneProps ?? {},
        lineTwo: ({ row }) => row.beneficiary.lineTwo,
        rightContent: ({ row }) => row.beneficiary.rightContent,
        textAccompaniment: ({ row }) => row.beneficiary.textAccompaniment,
      }),
      sortComparator: (
        v1: RowData['beneficiary'],
        v2: RowData['beneficiary'],
        p1,
        p2
      ) => {
        return gridStringOrNumberComparator(v1.lineOne, v2.lineOne, p1, p2);
      },
    },
  ];
}

export function getAgeBoundsCopy(
  ageBoundFragment:
    | TrustBeneficiariesList_ScheduledDistributionFragment
    | TrustBeneficiariesList_AccessParameterFragment
) {
  const getAgeBoundsCopyFromAgeBounds = (bounds: {
    ageRequirementStart?: number | null;
    ageRequirementEnd?: number | null;
  }) => {
    const hasAgeStart = !isNil(bounds.ageRequirementStart);
    const hasAgeEnd = !isNil(bounds.ageRequirementEnd);

    if (hasAgeStart && hasAgeEnd) {
      return `Between ages ${bounds.ageRequirementStart} and ${bounds.ageRequirementEnd}`;
    }

    if (hasAgeStart && !hasAgeEnd) {
      return `Upon age ${bounds.ageRequirementStart}`;
    }

    if (!hasAgeStart && hasAgeEnd) {
      // Should not be used
      return `Until age ${bounds.ageRequirementEnd}`;
    }

    return '';
  };

  if (ageBoundFragment.__typename === 'ScheduledDistribution') {
    return getAgeBoundsCopyFromAgeBounds(ageBoundFragment);
  }

  if (ageBoundFragment.__typename === 'AccessParameter') {
    const ageParams = getNodes(ageBoundFragment.accessAgeParameters);

    return ageParams
      .map((param) => getAgeBoundsCopyFromAgeBounds(param))
      .join(', ');
  }

  return '';
}

export function getAccessParameterCopy(
  parameter: Pick<
    TrustBeneficiariesList_AccessParameterFragment,
    'kind' | 'percentage' | 'amount' | 'frequency'
  >,
  entityKind?: EntityKind
) {
  const getFrequencyCopy = (frequency: AccessParameterFrequency) => {
    switch (frequency) {
      case AccessParameterFrequency.Monthly:
        return 'monthly';
      case AccessParameterFrequency.Quarterly:
        return 'quarterly';
      case AccessParameterFrequency.Annually:
        return 'annually';
      case AccessParameterFrequency.Semiannually:
        return 'semiannually';
      default:
        throw new UnreachableError({
          case: frequency,
          message: `Unrecognized AccessParameterFrequency ${frequency}`,
        });
    }
  };

  const appendFrequency = (copy: string) => {
    if (parameter.frequency) {
      return `${copy} ${getFrequencyCopy(parameter.frequency)}`;
    }
    return copy;
  };

  switch (parameter.kind) {
    case AccessParameterKind.Full:
      return 'Full access';
    case AccessParameterKind.AllTrustIncome: {
      if (parameter.percentage) {
        return `Access to ${formatPercentNoDecimals(parameter.percentage)}% of trust income`;
      }
      return 'Access to all trust income';
    }
    case AccessParameterKind.Percentage:
      if (!parameter.percentage) {
        const msg = `AccessParameterKind.Percentage has no percentage`;
        diagnostics.error(msg, new Error(msg));

        return '';
      }

      if (
        entityKind === EntityKind.CltTrust ||
        entityKind === EntityKind.CrtTrust
      ) {
        return `${formatPercent(parameter.percentage, 0)}% of the trust`;
      }

      return appendFrequency(
        `Access to ${formatPercent(parameter.percentage, 0)}% of the trust`
      );
    case AccessParameterKind.Amount:
      if (!parameter.amount) {
        const msg = `AccessParameterKind.Amount has no amount`;
        diagnostics.error(msg, new Error(msg));

        return '';
      }

      if (
        entityKind === EntityKind.CltTrust ||
        entityKind === EntityKind.CrtTrust
      ) {
        return `${formatCurrencyNoDecimals(parameter.amount)}`;
      }

      return appendFrequency(
        `Access to ${formatCurrencyNoDecimals(parameter.amount)}`
      );
    case AccessParameterKind.FullDiscretion:
      return 'Access under full discretion of trustee';
    case AccessParameterKind.Hems:
      return 'Access under HEMS';
    case AccessParameterKind.Hms:
      return 'Access under HMS';
    case AccessParameterKind.Other:
      return 'Access under other parameters';
    case AccessParameterKind.NetIncome:
      return 'Access to net income';
    case AccessParameterKind.NetIncomeWithMakeup:
      return 'Access to net income with makeup';
    case AccessParameterKind.TrustIncome:
      return '';
    default:
      throw new UnreachableError({
        case: parameter.kind,
        message: `Unrecognized access parameter kind ${parameter.kind}`,
      });
  }
}

export function getScheduledDistributionCopy(
  distribution: TrustBeneficiariesList_ScheduledDistributionFragment
) {
  const getFrequencyCopy = (frequency: ScheduledDistributionFrequency) => {
    switch (frequency) {
      case ScheduledDistributionFrequency.Monthly:
        return 'monthly';
      case ScheduledDistributionFrequency.Quarterly:
        return 'quarterly';
      case ScheduledDistributionFrequency.Annually:
        return 'annually';
      case ScheduledDistributionFrequency.Semiannually:
        return 'semiannually';
      case ScheduledDistributionFrequency.OneTime:
        return 'one time';
      default:
        throw new UnreachableError({
          case: frequency,
          message: `Unrecognized ScheduledDistributionFrequency ${frequency}`,
        });
    }
  };

  const appendFrequency = (copy: string) => {
    if (distribution.frequency) {
      return `${copy} ${getFrequencyCopy(distribution.frequency)}`;
    }
    return copy;
  };

  const inflationSuffix = distribution.growByInflation
    ? ', inflation adjusted'
    : '';

  switch (distribution.kind) {
    case ScheduledDistributionKind.AllIncome: {
      const isOneTime =
        distribution.frequency === ScheduledDistributionFrequency.OneTime;
      if (distribution.percentage) {
        const base = `${formatPercentNoDecimals(distribution.percentage)}% of trust income`;
        return isOneTime
          ? `One-time scheduled distribution of ${base}${inflationSuffix}`
          : `${appendFrequency(base)}${inflationSuffix}`;
      } else {
        return isOneTime
          ? `One-time scheduled distribution of all income${inflationSuffix}`
          : `${appendFrequency('All income')}${inflationSuffix}`;
      }
    }
    case ScheduledDistributionKind.Amount: {
      if (!distribution.amount) {
        const msg = `ScheduledDistributionKind.Amount has no amount`;
        diagnostics.error(msg, new Error(msg));

        return '';
      }
      const isOneTime =
        distribution.frequency === ScheduledDistributionFrequency.OneTime;
      const base = formatCurrencyNoDecimals(distribution.amount);
      return isOneTime
        ? `One-time scheduled distribution of ${base}${inflationSuffix}`
        : `${appendFrequency(base)}${inflationSuffix}`;
    }
    case ScheduledDistributionKind.Percentage:
      if (!distribution.percentage) {
        const msg = `ScheduledDistributionKind.Percentage has no percentage`;
        diagnostics.error(msg, new Error(msg));

        return '';
      }
      if (distribution.frequency === ScheduledDistributionFrequency.OneTime) {
        return `One-time scheduled distribution of ${formatPercent(distribution.percentage, 0)}% of the trust${inflationSuffix}`;
      }
      return `${appendFrequency(
        `Scheduled distribution of ${formatPercent(distribution.percentage, 0)}% of the trust`
      )}${inflationSuffix}`;
    default:
      throw new UnreachableError({
        case: distribution.kind,
        message: `Unrecognized scheduled distribution kind ${distribution.kind}`,
      });
  }
}

export function getRows(
  beneficiaries: TrustBeneficiariesList_BeneficiaryFragment[],
  entityKind?: EntityKind
): RowData[] {
  const beneficiaryRows: RowData[] = [];
  const accessAndDistributionRows: RowData[] = [];

  const orderedBeneficiaries = sortBeneficiariesByLevel(beneficiaries);

  orderedBeneficiaries.forEach((beneficiary) => {
    const { heading } = createBeneficiaryRow(beneficiary);

    beneficiaryRows.push({
      id: beneficiary.id,
      path: [beneficiary.id],
      beneficiary: {
        lineOne: heading,
        lineOneProps: {
          variant: 'title-xs',
        },
        lineTwo: beneficiary.level
          ? (BENEFICIARY_LEVEL_CURRENT_ACCESS_DISPLAY_NAMES[
              beneficiary.level
            ] ?? undefined)
          : undefined,
        rightContent: beneficiary.notes ? (
          <ContextualHelpTooltip
            wrappedComponent={
              <Badge
                variant={BadgeVariants.SecondaryOutline}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 0.25,
                }}
                display={
                  <>
                    <SearchSmIcon size={12} color={COLORS.NAVY[700]} />
                    <Typography
                      variant="title-xs"
                      fontWeight={FONT_WEIGHTS.regular}
                      component="span"
                      color={COLORS.TEXT.PRIMARY}
                    >
                      Notes
                    </Typography>
                  </>
                }
              />
            }
          >
            <PopperContent body={beneficiary.notes} />
          </ContextualHelpTooltip>
        ) : undefined,
      },
    });

    mapBeneficiaryRows(beneficiary, entityKind).forEach((row) =>
      accessAndDistributionRows.push(row)
    );
  });

  return [...beneficiaryRows, ...accessAndDistributionRows];
}

type AccessOrDistributionType =
  | TrustBeneficiariesList_AccessParameterFragment
  | TrustBeneficiariesList_ScheduledDistributionFragment;

function mapBeneficiaryRows(
  beneficiary: TrustBeneficiariesList_BeneficiaryFragment,
  entityKind: EntityKind | undefined
): RowData[] {
  const {
    scheduledDistributions,
    accessParameters,
    id: beneficiaryId,
  } = beneficiary;

  const allAccessAndDistributions: AccessOrDistributionType[] = compact([
    ...getNodes(scheduledDistributions),
    ...getNodes(accessParameters),
  ]).sort(sortBeneficiaryAccessAndDistributions);

  return compact(
    allAccessAndDistributions.map((item) => {
      if (item.__typename === 'AccessParameter') {
        return mapAccessParameterToRow(item, beneficiaryId, entityKind);
      } else if (item.__typename === 'ScheduledDistribution') {
        return mapScheduledDistributionToRow(item, beneficiaryId);
      } else {
        return null;
      }
    })
  );
}

interface DistributionInfo {
  kind: ScheduledDistributionKind | AccessParameterKind;
  ageRequirementStart: number | undefined | null;
  ageRequirementEnd: number | undefined | null;
}

/**
 * Sort a beneficiary's access parameters and scheduled distributions
 * based on the age of access.
 *
 * - Display cases with no age-based access first
 * - Then display cases with only an end age; if both have an end age, the younger age should come first
 * - Then display cases with both start and end ages; if both have start and end dates,
 *   the youngest end date comes first; if both end dates are the same, the youngest start date comes first
 * - Then display cases with a start age; if both have a start age, the younger age should come first
 */
export function sortBeneficiaryAccessAndDistributions(
  aInput: AccessOrDistributionType,
  bInput: AccessOrDistributionType
): number {
  const a = getDistributionInfoFromAccessDistributionType(aInput);
  const b = getDistributionInfoFromAccessDistributionType(bInput);

  if (a && !b) {
    return -1;
  } else if (!a && b) {
    return 1;
  }

  if (!a || !b) {
    return 0;
  }

  if (
    a.kind === AccessParameterKind.Full &&
    b.kind !== AccessParameterKind.Full
  ) {
    return -1;
  } else if (
    a.kind !== AccessParameterKind.Full &&
    b.kind === AccessParameterKind.Full
  ) {
    return 1;
  }

  const aHasNoAge = !a.ageRequirementStart && !a.ageRequirementEnd;
  const bHasNoAge = !b.ageRequirementStart && !b.ageRequirementEnd;

  if (aHasNoAge && !bHasNoAge) {
    return -1;
  } else if (!aHasNoAge && bHasNoAge) {
    return 1;
  }

  const aIsUntil = !a.ageRequirementStart && a.ageRequirementEnd;
  const bIsUntil = !b.ageRequirementStart && b.ageRequirementEnd;

  const aIsUpon = a.ageRequirementStart && !a.ageRequirementEnd;
  const bIsUpon = b.ageRequirementStart && !b.ageRequirementEnd;

  const aIsBetween = a.ageRequirementStart && a.ageRequirementEnd;
  const bIsBetween = b.ageRequirementStart && b.ageRequirementEnd;

  if (aIsUntil && !bIsUntil) {
    return -1;
  } else if (!aIsUntil && bIsUntil) {
    return 1;
  }

  if (aIsUntil && bIsUntil && a.ageRequirementEnd && b.ageRequirementEnd) {
    return a.ageRequirementEnd - b.ageRequirementEnd;
  }

  if (aIsBetween && !bIsBetween) {
    return -1;
  } else if (!aIsBetween && bIsBetween) {
    return 1;
  }

  if (
    aIsBetween &&
    bIsBetween &&
    a.ageRequirementEnd &&
    b.ageRequirementEnd &&
    a.ageRequirementStart &&
    b.ageRequirementStart
  ) {
    const endDiff = a.ageRequirementEnd - b.ageRequirementEnd;
    if (endDiff !== 0) {
      return endDiff;
    }
    return a.ageRequirementStart - b.ageRequirementStart;
  }

  if (aIsUpon && !bIsUpon) {
    return -1;
  } else if (!aIsUpon && bIsUpon) {
    return 1;
  }

  if (aIsUpon && bIsUpon && a.ageRequirementStart && b.ageRequirementStart) {
    return a.ageRequirementStart - b.ageRequirementStart;
  }

  return 0;
}

function getDistributionInfoFromAccessDistributionType(
  input: AccessOrDistributionType
): DistributionInfo | null {
  let output: DistributionInfo | null = null;

  if (input.__typename === 'AccessParameter') {
    const relevantAccessParameter = first(getNodes(input.accessAgeParameters));
    output = {
      kind: input.kind,
      ageRequirementStart: relevantAccessParameter?.ageRequirementStart,
      ageRequirementEnd: relevantAccessParameter?.ageRequirementEnd,
    };
  } else if (input.__typename === 'ScheduledDistribution') {
    output = {
      kind: input.kind,
      ageRequirementStart: input.ageRequirementStart,
      ageRequirementEnd: input.ageRequirementEnd,
    };
  }

  return output;
}

function mapScheduledDistributionToRow(
  distribution: TrustBeneficiariesList_ScheduledDistributionFragment,
  beneficiaryId: string
): RowData {
  const output: RowData = {
    id: distribution.id,
    path: [beneficiaryId, distribution.id],
    beneficiary: {
      lineOne: getScheduledDistributionCopy(distribution),
      lineTwo: getAgeBoundsCopy(distribution),
      rightContent: (
        <Badge
          sx={{ whiteSpace: 'nowrap' }}
          variant={BadgeVariants.Primary}
          display="Scheduled distribution"
        />
      ),
    },
  };
  return output;
}

function mapAccessParameterToRow(
  parameter: TrustBeneficiariesList_AccessParameterFragment,
  beneficiaryId: string,
  entityKind: EntityKind | undefined
): RowData {
  let rightContent = null;
  if (parameter.kind === AccessParameterKind.Full) {
    rightContent = (
      <Badge
        sx={{ whiteSpace: 'nowrap' }}
        variant={BadgeVariants.Primary}
        display="Full access"
      />
    );
  } else {
    rightContent = (
      <Badge
        sx={{ whiteSpace: 'nowrap' }}
        variant={BadgeVariants.SecondaryFilled}
        display={
          entityKind === EntityKind.CltTrust ||
          entityKind === EntityKind.CrtTrust
            ? 'Scheduled distribution'
            : 'Partial access'
        }
      />
    );
  }

  const output: RowData = {
    id: parameter.id,
    path: [beneficiaryId, parameter.id],
    beneficiary: {
      lineOne: getAccessParameterCopy(parameter, entityKind),
      lineTwo: getAgeBoundsCopy(parameter),
      rightContent,
    },
  };

  return output;
}
